import { useQueries, useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";

// Hooks
import { useAuthorization } from "@Common/hooks/Authorization/Authorization";
import { useLocationId } from "@Common/hooks/Location/useLocationId";

// APIs
import { getSalesPerformancesByCommissionCycle } from "@DataPages/api/salesOperations/salesPerformance";

// Helpers
import { and, or } from "@Common/helpers/sonar";

// API
import { getSalesLog } from "@DataPages/api/salesOperations/salesLog";
import { getSalesLogAnalytics } from "@DataPages/api/salesOperations/salesLog/queries";

// Context
import { SalesLogChartProvider } from "@DataPages/context/salesOperations/salesLog/SalesLogChartsContext";

// Components
import SalesLogTableSection from "@DataPages/components/SalesOperations/SalesLogTableSection.tsx";
import SalesLogAnalyticsSection from "@DataPages/components/SalesOperations/Tabs/SalesLog/SalesLogAnalyticsSection";
import CarryOverFundingSection from "@DataPages/components/SalesOperations/Tabs/SalesPerformance/components/CarryOverFundingSection";
import SalesPerformanceSection from "@DataPages/components/SalesOperations/Tabs/SalesPerformance/SalesPerformanceSection";
import RoundedBox from "@Layouts/components/Basics/RoundedBox";
import TabSelectable from "@Layouts/components/Tab/TabSelectable";

// Constants
import actionsList from "@Constants/actionsList";
import { getSalesOperationName } from "@DataPages/components/SalesOperations/constants";

// Types
import { ICommissionCycle } from "@Common/api/types/CommissionCycle";
import { ISidebarItem } from "@Common/interfaces/sidebar";
import { Nullable } from "@Common/interfaces/types";

type SalesOperationsModelProps = {
	selectedCommissionCycle: Nullable<ISidebarItem>;
};

type TabInfo = {
	title: string;
	index: number;
	disabled: boolean;
};

enum ETabLabel {
	SalesPerformance = "Sales Performance",
	SalesLog = "Sales Log",
}

function getTabIndex(tabLabel: ETabLabel): number {
	return Object.values(ETabLabel).indexOf(tabLabel);
}

const SALES_PERFORMANCE_QUERY_KEY = getSalesOperationName("QUERY", "SALES_PERFORMANCE");
const SALES_LOG_SALES_QUERY_KEY = getSalesOperationName("QUERY", "SALES_LOG_SALES");
const SALES_LOG_ANALYTICS_QUERY_KEY = getSalesOperationName("QUERY", "SALES_LOG_ANALYTICS");

export default function SalesOperationsModel(props: Readonly<SalesOperationsModelProps>) {
	const { useAuth } = useAuthorization();

	const locationId = useLocationId();

	const hasSalesPerformanceAccess = useAuth(actionsList.VIEW_SALES_PERFORMANCE_REPORTS);
	const hasSalesLogAccess = useAuth(actionsList.VIEW_SALES_LOG);

	/********************************************* HELPERS ************************************************/
	const isTabEnabled = useCallback(
		(tab: ETabLabel): boolean => {
			switch (tab) {
				case ETabLabel.SalesPerformance: // Sales Performance
					return hasSalesPerformanceAccess;
				case ETabLabel.SalesLog: // Sales Log
					return hasSalesLogAccess;
				default:
					return false;
			}
		},
		[hasSalesPerformanceAccess, hasSalesLogAccess]
	);

	const firstTabWithAccess = Object.values(ETabLabel).find(isTabEnabled) ?? ETabLabel.SalesPerformance;
	const [tabSelected, setTabSelected] = useState<TabInfo>(() => ({
		title: firstTabWithAccess,
		index: getTabIndex(firstTabWithAccess),
		disabled: !isTabEnabled(firstTabWithAccess),
	}));
	/********************************************* QUERIES ************************************************/
	const { data: performanceData, isLoading: isLoadingPerformanceData } = useQuery({
		queryKey: [SALES_PERFORMANCE_QUERY_KEY, locationId, props.selectedCommissionCycle?._id],
		queryFn: async () => getSalesPerformancesByCommissionCycle(locationId, props.selectedCommissionCycle?._id as string),
		enabled: and(!!locationId, !!props.selectedCommissionCycle?._id, tabSelected.title === ETabLabel.SalesPerformance),
		staleTime: Infinity,
	});

	const [{ data: salesLogs, isLoading: isLoadingSales }, { data: salesLogAnalytics, isLoading: isLoadingAnalytic }] = useQueries({
		queries: [
			{
				queryKey: [SALES_LOG_SALES_QUERY_KEY, locationId, props.selectedCommissionCycle?._id],
				queryFn: () => getSalesLog(locationId, props.selectedCommissionCycle?._id as string),
				enabled: and(Boolean(locationId), Boolean(props.selectedCommissionCycle?._id), tabSelected.title === ETabLabel.SalesLog),
				staleTime: Infinity,
			},
			{
				queryKey: [SALES_LOG_ANALYTICS_QUERY_KEY, locationId, props.selectedCommissionCycle?._id],
				queryFn: () => getSalesLogAnalytics(locationId, props.selectedCommissionCycle?._id as string),
				enabled: and(Boolean(locationId), Boolean(props.selectedCommissionCycle?._id), tabSelected.title === ETabLabel.SalesLog),
				staleTime: Infinity,
			},
		],
	});

	const tabs = Object.values(ETabLabel)
		.filter(Boolean)
		.map((value) => ({ title: value, index: getTabIndex(value), disabled: !isTabEnabled(value) }));

	return (
		<SalesLogChartProvider>
			<RoundedBox className={"!p-0 !pt-8 !h-fit mb-4"}>
				<TabSelectable
					tabs={tabs}
					tabBarClassName={"!p-0 !m-0"}
					tabsProps={{
						tabClasses: "!min-w-[1100px]",
					}}
					content={[
						<SalesPerformanceSection
							key={ETabLabel.SalesPerformance}
							selected={props.selectedCommissionCycle}
							isLoading={or(!props.selectedCommissionCycle, isLoadingPerformanceData)}
							dataPresent={Boolean(props.selectedCommissionCycle)}
							data={performanceData?.salesPerformance}
						/>,
						<SalesLogAnalyticsSection
							key={ETabLabel.SalesLog}
							selectedCommissionCycle={props.selectedCommissionCycle as unknown as ICommissionCycle}
							dataPresent={Boolean(props.selectedCommissionCycle)}
							isLoading={or(!props.selectedCommissionCycle, isLoadingAnalytic)}
							salesLogs={salesLogs ?? []}
							salesLogAnalytics={salesLogAnalytics}
						/>,
					]}
					tabSelected={tabSelected.index}
					onTabChange={(_, tabInfo) => {
						setTabSelected(tabInfo.label);
					}}
				/>
			</RoundedBox>
			{tabSelected.title === ETabLabel.SalesPerformance && (
				<RoundedBox className="!h-fit !pb-[48px]">
					<CarryOverFundingSection
						data={performanceData?.carryOverFundingProgress}
						isLoading={or(!props.selectedCommissionCycle, isLoadingPerformanceData)}
						selected={props.selectedCommissionCycle}
					/>
				</RoundedBox>
			)}
			{tabSelected.title === ETabLabel.SalesLog && (
				<SalesLogTableSection
					salesLogs={salesLogs ?? []}
					selectedCommissionCycle={props.selectedCommissionCycle as unknown as ICommissionCycle}
					isLoading={or(!props.selectedCommissionCycle, isLoadingSales)}
				/>
			)}
		</SalesLogChartProvider>
	);
}
